import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  FileText,
  Loader2,
  Play,
  Receipt,
  Smartphone,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { DataTable, type Column } from '../components/DataTable';
import { EmptyState } from '../components/EmptyState';
import { Filters, useFilters, type FilterField } from '../components/Filters';
import { SelectionBar, type BulkAction } from '../components/SelectionBar';
import { HelpHint } from '../components/HelpHint';
import { Tooltip } from '../components/Hover';
import { Modal } from '../components/Modal';
import { PageNav } from '../components/PageNav';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import type * as T from '../lib/types';

const STATUS_TONE: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  issued: 'bg-navy-50 text-navy-700',
  part_paid: 'bg-amber-50 text-amber-700',
  paid: 'bg-emerald-50 text-emerald-700',
  void: 'bg-muted text-muted-foreground line-through',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  issued: 'Unpaid',
  part_paid: 'Part paid',
  paid: 'Paid',
  void: 'Cancelled',
};

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Invoices, and taking the money.
 *
 * ## Why the M-Pesa button waits
 *
 * Sending a prompt is not the same as being paid. The button starts a push and
 * then polls until Safaricom says what happened, because the alternative — a
 * screen that says "sent" and moves on — leaves an agent with a customer in
 * front of them and no idea whether to let them go. The three outcomes are
 * spelled out rather than reduced to success or failure, since "they cancelled
 * it on their phone" and "there was no money in the account" need different
 * next sentences from the person at the counter.
 */
export function BillingPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [paying, setPaying] = useState<T.InvoiceRow | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const invoices = useQuery({
    queryKey: ['invoices', status],
    queryFn: () => api.invoices({ status: status || undefined }),
  });

  const readiness = useQuery({
    queryKey: ['mpesa-readiness'],
    queryFn: () => api.mpesaReadiness(),
    enabled: can('payment:collect'),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['invoices'] });

  const run = useMutation({
    mutationFn: () => api.runBilling(),
    onSuccess: async (result) => {
      setError(null);
      setNotice(result.summary);
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const all = invoices.data ?? [];

  const FIELDS: FilterField<T.InvoiceRow>[] = [
    { key: 'number', label: 'Invoice', type: 'text', value: (row) => row.number },
    { key: 'customer', label: 'Customer', type: 'text', value: (row) => row.customerName },
    { key: 'period', label: 'Period', type: 'text', value: (row) => row.period ?? null },
    { key: 'due', label: 'Due', type: 'date', value: (row) => row.dueDate },
    { key: 'total', label: 'Total', type: 'number', value: (row) => row.totalCents / 100 },
    {
      key: 'outstanding',
      label: 'Outstanding',
      type: 'number',
      value: (row) => row.outstandingCents / 100,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'choice',
      options: Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })),
      value: (row) => row.status,
    },
  ];

  const { query, setQuery, clauses, setClauses, filtered: rows } = useFilters(all, FIELDS);

  /*
   * Of everything, not of what a filter is showing. A headline figure that
   * moves when somebody narrows the list is one nobody can quote.
   */
  const unpaid = all.filter((row) => row.outstandingCents > 0);
  const owed = unpaid.reduce((sum, row) => sum + row.outstandingCents, 0);

  /*
   * Export and print only.
   *
   * This is the run screen: it shows what a billing run produced. Changing an
   * invoice from here would be changing a document somebody was already sent,
   * and the way to do that is a credit note where the original stays visible
   * beside the correction. Asking for payment stays per row, because it sends
   * a prompt to a real person's phone and forty at once is not a thing anybody
   * should do by accident.
   */
  const actions: BulkAction<T.InvoiceRow>[] = [
    { key: 'print', label: 'Print', prominent: true, run: () => window.print() },
  ];

  const columns: Column<T.InvoiceRow>[] = [
    {
      key: 'number',
      header: 'Invoice',
      sortBy: (row) => row.number,
      cell: (row) => <span className="whitespace-nowrap font-medium">{row.number}</span>,
    },
    {
      key: 'customer',
      header: 'Customer',
      sortBy: (row) => row.customerName.toLowerCase(),
      cell: (row) => row.customerName,
    },
    {
      key: 'period',
      header: 'Period',
      secondary: true,
      sortBy: (row) => row.period ?? null,
      cell: (row) => <span className="whitespace-nowrap text-muted-foreground">{row.period ?? '—'}</span>,
    },
    {
      key: 'due',
      header: 'Due',
      secondary: true,
      // On the date itself — sorting the printed form puts 01/02 before 31/01.
      sortBy: (row) => new Date(row.dueDate).getTime(),
      cell: (row) => <span className="whitespace-nowrap text-muted-foreground">{day(row.dueDate)}</span>,
    },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      sortBy: (row) => row.totalCents,
      cell: (row) => <span className="whitespace-nowrap tabular-nums">{row.total}</span>,
    },
    {
      key: 'outstanding',
      header: 'Outstanding',
      numeric: true,
      sortBy: (row) => row.outstandingCents,
      cell: (row) => (
        <span className="whitespace-nowrap tabular-nums">
          {row.outstandingCents > 0 ? row.outstanding : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortBy: (row) => row.status,
      cell: (row) => (
        <span className={`badge ${STATUS_TONE[row.status] ?? 'bg-muted'}`}>
          {STATUS_LABEL[row.status] ?? row.status}
        </span>
      ),
    },
    {
      key: 'ask',
      header: '',
      cell: (row) =>
        row.outstandingCents > 0 && can('payment:collect') ? (
          <Tooltip label="Sends an M-Pesa prompt to the customer's phone" side="left">
            <button
              type="button"
              className="btn-quiet text-xs print:hidden"
              onClick={(event) => {
                event.stopPropagation();
                setPaying(row);
                setError(null);
                setNotice(null);
              }}
            >
              <Smartphone className="h-3.5 w-3.5" aria-hidden="true" />
              Ask for payment
            </button>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageNav crumbs={[{ label: 'Billing' }]} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-1.5 font-heading text-2xl">
            Billing
            <HelpHint>
              Every active subscription is invoiced on its own billing day. Running the billing
              twice is safe — an invoice that already exists is never raised again.
            </HelpHint>
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {unpaid.length === 0
              ? 'Nothing outstanding.'
              : `${unpaid.length} unpaid invoice${unpaid.length === 1 ? '' : 's'}, ${new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', maximumFractionDigits: 0 }).format(owed / 100)} owed.`}
          </p>
        </div>

        {can('billing:issue') && (
          <button
            type="button"
            className="btn-primary"
            disabled={run.isPending}
            onClick={() => run.mutate()}
          >
            {run.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Play className="h-4 w-4" aria-hidden="true" />
            )}
            Run the billing
          </button>
        )}
      </div>

      {readiness.data && !readiness.data.configured && can('payment:collect') && (
        <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {readiness.data.message}
        </p>
      )}

      {notice && (
        <p className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {notice}
        </p>
      )}
      {error && (
        <p className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          Show
          <select
            className="input mt-1"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Everything</option>
            <option value="issued">Unpaid</option>
            <option value="part_paid">Part paid</option>
            <option value="paid">Paid</option>
            <option value="void">Cancelled</option>
          </select>
        </label>
      </div>

      {invoices.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the invoices
        </p>
      )}

      {invoices.data && rows.length === 0 && (
        <EmptyState
          icon={FileText}
          headline={status ? 'Nothing matches that' : 'No invoices yet'}
          body={
            status
              ? 'Try showing everything instead.'
              : 'Put a customer on a package, then run the billing. Nothing is invoiced until somebody asks for it.'
          }
        />
      )}

      {rows.length > 0 && (
        <>
          <Filters<T.InvoiceRow>
            fields={FIELDS}
            query={query}
            onQuery={setQuery}
            clauses={clauses}
            onClauses={setClauses}
            total={all.length}
            showing={rows.length}
            placeholder="Search by invoice or customer"
          />

          <SelectionBar<T.InvoiceRow>
            rows={rows}
            selected={selected}
            onClear={() => setSelected(new Set())}
            rowKey={(row) => row.id}
            exportName="billing"
            csv={[
              { header: 'Invoice', value: (row) => row.number },
              { header: 'Customer', value: (row) => row.customerName },
              { header: 'Period', value: (row) => row.period ?? '' },
              { header: 'Due', value: (row) => row.dueDate.slice(0, 10) },
              { header: 'Total', value: (row) => row.totalCents / 100 },
              { header: 'Outstanding', value: (row) => row.outstandingCents / 100 },
              { header: 'Status', value: (row) => STATUS_LABEL[row.status] ?? row.status },
            ]}
            actions={actions}
          />

          <DataTable<T.InvoiceRow>
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            selected={selected}
            onSelect={setSelected}
            defaultSort={{ key: 'due' }}
            caption={`${rows.length} ${rows.length === 1 ? 'invoice' : 'invoices'} in this run`}
            empty={<p className="py-6 text-center text-sm text-muted-foreground">Nothing here.</p>}
          />
        </>
      )}

      {paying && (
        <CollectDialog
          invoice={paying}
          onClose={() => setPaying(null)}
          onDone={async (message) => {
            setNotice(message);
            setPaying(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

/**
 * Send the prompt, then wait for it.
 *
 * Polls every three seconds for two minutes. Both numbers come from how M-Pesa
 * actually behaves: a customer takes twenty to forty seconds to find their
 * phone and enter a PIN, and Safaricom gives up at about ninety. Stopping at
 * two minutes means the dialog never sits open forever on a prompt that will
 * never be answered — and the message says what to do next rather than leaving
 * an agent guessing.
 */
function CollectDialog({
  invoice,
  onClose,
  onDone,
}: {
  invoice: T.InvoiceRow;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [waited, setWaited] = useState(0);

  const start = useMutation({
    mutationFn: () =>
      api.collectPayment({
        customerId: invoice.customerId,
        amountCents: invoice.outstandingCents,
      }),
    onSuccess: (result) => {
      setError(null);
      setPaymentId(result.paymentId);
    },
    onError: (e: Error) => setError(e.message),
  });

  const poll = useQuery({
    queryKey: ['payment-status', paymentId],
    queryFn: () => api.paymentStatus(paymentId as string),
    enabled: paymentId !== null && waited < 120,
    refetchInterval: 3000,
  });

  useEffect(() => {
    if (!paymentId || poll.data?.status !== 'pending') return;
    const timer = setInterval(() => setWaited((value) => value + 3), 3000);
    return () => clearInterval(timer);
  }, [paymentId, poll.data?.status]);

  useEffect(() => {
    if (poll.data?.status === 'confirmed') onDone(poll.data.message);
  }, [poll.data?.status, poll.data?.message, onDone]);

  const settled = poll.data?.status === 'failed';
  const timedOut = waited >= 120 && poll.data?.status === 'pending';

  // Through `Modal`, which portals out of `main`'s stacking context — see
  // the note there for the bug that made this necessary.
  return (
    <Modal
      title="Ask for payment"
      subtitle={`${invoice.customerName} — ${invoice.outstanding} on ${invoice.number}`}
      onClose={onClose}
    >
      <>

        {!paymentId && !error && (
          <p className="mt-4 text-sm">
            An M-Pesa prompt will go to <strong>{invoice.customerPhone}</strong>. They enter their
            PIN and the invoice settles itself.
          </p>
        )}

        {paymentId && poll.data?.status === 'pending' && !timedOut && (
          <p className="mt-4 flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Waiting for them to enter their PIN…
          </p>
        )}

        {timedOut && (
          <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            No answer yet. The prompt may still be on their phone — check the invoice again in a
            minute rather than sending a second one.
          </p>
        )}

        {/* Money moved but not the money asked for: a person checks it, so no retry. */}
        {poll.data?.status === 'held' && (
          <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {poll.data?.message}
          </p>
        )}

        {settled && (
          <p className="mt-4 flex items-start gap-2 rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {poll.data?.message}
          </p>
        )}

        {error && (
          <p className="mt-4 rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {error}
          </p>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" className="btn-quiet" onClick={onClose}>
            {paymentId ? 'Close' : 'Not now'}
          </button>
          {(!paymentId || settled || timedOut) && (
            <button
              type="button"
              className="btn-primary"
              disabled={start.isPending}
              onClick={() => {
                setWaited(0);
                start.mutate();
              }}
            >
              {start.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Smartphone className="h-4 w-4" aria-hidden="true" />
              )}
              {settled || timedOut ? 'Try again' : 'Send the prompt'}
            </button>
          )}
        </div>
      </>
    </Modal>
  );
}

/**
 * Recording money that arrived some other way.
 *
 * Exported separately because it belongs on the customer page as much as here:
 * somebody walks in with cash, or a bank transfer lands, and the person
 * recording it is looking at the customer rather than at a list of invoices.
 */
export function RecordPaymentForm({
  customerId,
  onRecorded,
}: {
  customerId: string;
  onRecorded?: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'bank' | 'cash' | 'mpesa' | 'credit'>('bank');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: () =>
      api.recordPayment({
        customerId,
        // Shillings on screen, cents on the wire. The conversion happens once,
        // here, rather than in four places that might round differently.
        amountCents: Math.round(Number(amount) * 100),
        method,
        reference: reference.trim() || undefined,
      }),
    onSuccess: async (result) => {
      setError(null);
      setAmount('');
      setReference('');
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      await queryClient.invalidateQueries({ queryKey: ['arrears'] });
      onRecorded?.(result.message);
    },
    onError: (e: Error) => setError(e.message),
  });

  const needsReference = method === 'bank' || method === 'mpesa';

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        record.mutate();
      }}
    >
      <div className="flex flex-wrap gap-3">
        <label className="text-sm">
          Amount (KES)
          <input
            className="input mt-1 w-36 tabular-nums"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="1499"
          />
        </label>

        <label className="text-sm">
          How it arrived
          <select
            className="input mt-1"
            value={method}
            onChange={(event) => setMethod(event.target.value as typeof method)}
          >
            <option value="bank">Bank transfer</option>
            <option value="cash">Cash</option>
            <option value="mpesa">M-Pesa (already paid)</option>
            <option value="credit">Credit note</option>
          </select>
        </label>

        <label className="min-w-48 flex-1 text-sm">
          {method === 'mpesa' ? 'M-Pesa receipt' : 'Reference'}
          {!needsReference && <span className="text-muted-foreground"> (optional)</span>}
          <input
            className="input mt-1"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder={method === 'mpesa' ? 'QK12AB34CD' : 'Slip number'}
          />
        </label>
      </div>

      {error && (
        <p className="rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          className="btn-secondary"
          disabled={record.isPending || !amount.trim() || (needsReference && !reference.trim())}
        >
          {record.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Banknote className="h-4 w-4" aria-hidden="true" />
          )}
          Record it
        </button>
        <span className="text-xs text-muted-foreground">
          <Receipt className="mr-1 inline h-3 w-3" aria-hidden="true" />
          Applied to the oldest unpaid invoice first.
        </span>
      </div>
    </form>
  );
}
